import React, { useCallback } from 'react';

import { DeleteIcon } from '@sharedCR/coreDesignSystem/icons';
import ButtonWithTooltip from 'app/shared/components/common/ButtonWithTooltip';
import { FormAutocompleteOption } from 'app/shared/components/form/Autocomplete';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { FieldTypesEnum } from 'constants/loanActionEditor';
import { deleteByIndex, setIn } from 'utils/data';
import { caseInsensitiveIncludes } from 'utils/string';

import { loanManagementTestIds } from '../loanManagement.testIds';

import useFormContext from './hooks/useFormContext';
import { DynamicValueInput } from './LoanActionItems';
import { groupFieldsFunction } from './LoanActionsGroup.utils';

const LoanActionTagRow = ({
  tagIndex,
  rootPath,
  tagsOptions,
  isDisabled = false,
  isEditAllowed,
  isSingleItem,
}) => {
  const tagKey = `${rootPath}[${tagIndex}]`;

  const { form, setInForm, setValidationErrors } = useFormContext();

  const tags = _.get(form, rootPath);

  const onDeleteRow = useCallback(() => {
    const newTags = [...tags];
    newTags.splice(tagIndex, 1);
    setInForm(rootPath, newTags);
    setValidationErrors(errors =>
      setIn(errors, rootPath, deleteByIndex(_.get(errors, rootPath, []), tagIndex)),
    );
  }, [setInForm, setValidationErrors, tagIndex, tags, rootPath]);

  const onFilterDeletedTags = useCallback(
    (options, { inputValue }) =>
      options?.filter(
        ({ name, isDeleted }) => !isDeleted && caseInsensitiveIncludes(name, inputValue),
      ),
    [],
  );

  const onGetOptionSelected = useCallback((option, value) => option.value === value.value, []);

  return (
    <div className="flex" key={tagKey}>
      <DynamicValueInput
        required
        OptionContent={FormAutocompleteOption}
        className="grow"
        disabled={isDisabled || !isEditAllowed}
        filterOptions={onFilterDeletedTags}
        getOptionsSelected={onGetOptionSelected}
        groupBy={groupFieldsFunction}
        label="Tag"
        name={tagKey}
        options={tagsOptions}
        type={FieldTypesEnum.SELECT}
      />
      {isEditAllowed && !isSingleItem && (
        <ButtonWithTooltip
          data-testid={loanManagementTestIds.getDeleteRowButtonId(tagKey)}
          disabled={isDisabled}
          icon={DeleteIcon}
          size="medium"
          spanContainerClassName="flex items-center"
          title="Delete row"
          onClick={onDeleteRow}
        />
      )}
    </div>
  );
};

LoanActionTagRow.propTypes = {
  isSingleItem: PropTypes.bool.isRequired,
  rootPath: PropTypes.string.isRequired,
  tagIndex: PropTypes.number.isRequired,
  tagsOptions: PropTypes.array.isRequired,
  isEditAllowed: PropTypes.bool.isRequired,
  isDisabled: PropTypes.bool,
};

export default LoanActionTagRow;
